'use client';

import { CaretDown, Check } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { flushSync } from 'react-dom';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { setCompanySwitchGuardBypass } from '@/utils/companySwitchGuardBypass';
import { SwitchCompanyUnsavedModal } from '@/components/Modals/SwitchCompanyUnsavedModal/SwitchCompanyUnsavedModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue/useDebouncedValue';
import { useGetHouseList } from '@/data/house/useGetHouseList';
import { useHasAtgAdminCompanySwitchBlockingDirty } from '@/hooks/useRegisterAtgAdminCompanySwitchDirty/useRegisterAtgAdminCompanySwitchDirty';
import { useRouter } from '@/navigation-client';
import { useSwitchHouse } from '@/hooks/useSwitchHouse/useSwitchHouse';

type CompanySwitcherProps = Readonly<{
    houseId: string;
    houseName?: string;
    isOpen?: boolean;
    membershipHouseId: string;
    membershipHouseName?: string;
    onOpenChange?: (isOpen: boolean) => void;
}>;

type PendingCompanySwitch =
    | Readonly<{ houseId: string; targetHouseName: string; type: 'switchHouse' }>
    | Readonly<{ targetHouseName: string; type: 'goHome' }>;

export const CompanySwitcher = ({
    houseId,
    houseName,
    isOpen,
    membershipHouseId,
    membershipHouseName,
    onOpenChange,
}: CompanySwitcherProps) => {
    const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [pendingSwitch, setPendingSwitch] = useState<PendingCompanySwitch | null>(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const debouncedSearch = useDebouncedValue(search, 300);
    const containerRef = useRef<HTMLDivElement>(null);
    const { data } = useGetHouseList({ search: debouncedSearch });
    const houses = data?.data ?? [];
    const { replace } = useRouter();
    const { goHome, switchHouse } = useSwitchHouse();
    const hasBlockingDirtyForms = useHasAtgAdminCompanySwitchBlockingDirty();

    const isOverrideActive = houseId !== membershipHouseId;
    const resolvedIsOpen = isOpen ?? uncontrolledIsOpen;

    const setIsOpen = useCallback(
        (nextIsOpen: boolean) => {
            if (isOpen === undefined) {
                setUncontrolledIsOpen(nextIsOpen);
            }
            onOpenChange?.(nextIsOpen);
        },
        [isOpen, onOpenChange]
    );

    const closePanel = useCallback(() => {
        setIsOpen(false);
        setSearch('');
    }, [setIsOpen]);

    useEffect(() => {
        if (!resolvedIsOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (event.target instanceof Node && containerRef.current && !containerRef.current.contains(event.target)) {
                closePanel();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closePanel, resolvedIsOpen]);

    const handleToggle = () => {
        if (resolvedIsOpen) {
            setSearch('');
        }
        setIsOpen(!resolvedIsOpen);
    };

    const handleTextChange = useCallback((value: string) => {
        setSearch(value);
    }, []);

    const handleClear = useCallback(() => {
        setSearch('');
    }, []);

    const runSwitchHouse = (selectedHouseId: string) => {
        if (selectedHouseId !== houseId) {
            switchHouse(selectedHouseId);
            replace('/');
        }
        closePanel();
    };

    const runGoHome = () => {
        goHome();
        replace('/');
        closePanel();
    };

    const handleSelectHouse = (selectedHouseId: string) => {
        if (selectedHouseId === houseId) {
            closePanel();
            return;
        }
        if (hasBlockingDirtyForms) {
            const targetHouseName = houses.find((h) => h.id === selectedHouseId)?.name ?? '';
            setPendingSwitch({ houseId: selectedHouseId, targetHouseName, type: 'switchHouse' });
            setShowUnsavedModal(true);
            return;
        }
        runSwitchHouse(selectedHouseId);
    };

    const handleGoHome = () => {
        if (hasBlockingDirtyForms) {
            setPendingSwitch({ targetHouseName: membershipHouseName ?? '', type: 'goHome' });
            setShowUnsavedModal(true);
            return;
        }
        runGoHome();
    };

    const handleUnsavedModalCancel = () => {
        setShowUnsavedModal(false);
        setPendingSwitch(null);
    };

    const handleUnsavedModalConfirm = () => {
        const pending = pendingSwitch;
        // Close and unmount the modal synchronously before switchHouse(). removeQueries()
        // otherwise drives large subtree updates in the same turn and Hammer Modal can leave
        // the overlay open even though show is false.
        flushSync(() => {
            setShowUnsavedModal(false);
            setPendingSwitch(null);
        });
        // Signal useUnsavedChangesGuard to skip history.go(-1) in its cleanup.
        // router.replace('/') is deferred via startTransition so window.location.href has NOT
        // changed yet when queryClient.removeQueries() triggers the guard's cleanup.
        setCompanySwitchGuardBypass(true);
        if (pending?.type === 'switchHouse') {
            runSwitchHouse(pending.houseId);
        } else if (pending?.type === 'goHome') {
            runGoHome();
        }
    };

    return (
        <div
            className="relative"
            ref={containerRef}
        >
            {/* Popup options are native <button> elements (Tab/Enter), not a listbox widget — no arrow-key handler. */}
            <button
                aria-controls="company-switcher-list"
                aria-expanded={resolvedIsOpen}
                aria-haspopup="listbox"
                className="flex cursor-pointer items-center gap-xs border-0 bg-transparent p-0 text-sm text-text-inverse"
                data-testid="company-switcher-trigger"
                onClick={handleToggle}
                type="button"
            >
                <span>Viewing: {houseName || '…'}</span>
                <CaretDown
                    className={cn('size-4 text-text-inverse transition-transform', resolvedIsOpen && 'rotate-180')}
                    size="sm"
                />
            </button>
            {resolvedIsOpen && (
                <div
                    className="absolute top-full left-0 z-50 mt-xs w-[356px] rounded-md bg-background-primary py-2 shadow-04"
                    data-testid="company-switcher-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3">
                        <h6 className="m-0 text-sm font-semibold tracking-wide text-text-secondary uppercase">
                            Switch company context
                        </h6>
                        {isOverrideActive && (
                            <button
                                className="text-text-link cursor-pointer border-0 bg-transparent p-0 text-xs hover:underline"
                                data-testid="company-switcher-go-home"
                                onClick={handleGoHome}
                                type="button"
                            >
                                Return to my company
                            </button>
                        )}
                    </div>
                    <div className="px-2 pb-[10px]">
                        <SearchInput
                            className="w-full"
                            data-testid="company-search-input"
                            input-aria-label="Search by auction house name"
                            onClear={handleClear}
                            onTextChange={handleTextChange}
                            placeholder="Search by auction house name"
                            small
                            value={search}
                        />
                    </div>
                    <ul
                        aria-label="Company list"
                        className="m-0 max-h-64 list-none overflow-y-auto p-0"
                        id="company-switcher-list"
                    >
                        {houses.map((house) => {
                            const isActive = house.id === houseId;
                            return (
                                <li key={house.id}>
                                    <button
                                        aria-current={isActive ? 'true' : undefined}
                                        className={cn(
                                            'flex w-full cursor-pointer items-center border-0 py-1 text-left text-sm',
                                            isActive
                                                ? 'gap-[2px] bg-background-inverse px-2 text-text-inverse'
                                                : 'bg-transparent px-6 text-text-primary hover:bg-background-secondary'
                                        )}
                                        onClick={() => handleSelectHouse(house.id)}
                                        type="button"
                                    >
                                        {isActive && (
                                            <Check
                                                className="shrink-0 text-text-inverse"
                                                size="xs"
                                            />
                                        )}
                                        <span>{house.name}</span>
                                    </button>
                                </li>
                            );
                        })}
                        {houses.length === 0 && (
                            <li
                                aria-live="polite"
                                className="px-6 py-1 text-sm text-text-secondary"
                            >
                                No companies found
                            </li>
                        )}
                    </ul>
                </div>
            )}
            {showUnsavedModal && (
                <SwitchCompanyUnsavedModal
                    onCancel={handleUnsavedModalCancel}
                    onConfirm={handleUnsavedModalConfirm}
                    show
                    targetCompanyName={pendingSwitch?.targetHouseName ?? ''}
                />
            )}
        </div>
    );
};

CompanySwitcher.displayName = 'CompanySwitcher';
